import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { colors } from '../../lib/theme';

/**
 * Save Search opens a waitlist signup (accounts not shipped yet).
 * Posts to the same `/api/waitlist` endpoint as WaitlistSection.
 */
export function LoginModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message || 'Successfully joined waitlist!');
        setEmail('');
        setTimeout(() => {
          onClose();
          setStatus('idle');
          setMessage('');
        }, 2500);
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>
            Save your search
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-2xl hover:opacity-70 transition-opacity"
            style={{ color: colors.textMuted }}
          >
            ×
          </button>
        </div>

        <div className="text-center mb-6">
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Saved searches need an account, which isn&apos;t live yet. Join the waitlist and we&apos;ll
            email you when launch access opens.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Save search waitlist signup">
          <div>
            <label
              htmlFor="save-search-email"
              className="block text-sm font-medium mb-2"
              style={{ color: colors.textSecondary }}
            >
              Email (for waitlist)
            </label>
            <input
              id="save-search-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={status === 'loading'}
              className="w-full px-4 py-3 rounded-lg text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                backgroundColor: colors.bgTertiary,
                border: `1px solid ${colors.border}`,
                color: colors.textPrimary,
              }}
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading' || !email}
            className="w-full px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: status === 'success' ? colors.green : colors.blue,
              color: 'white',
              border: 'none',
            }}
          >
            {status === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin mx-auto" />
            ) : (
              'Join Waitlist for Early Access'
            )}
          </button>
        </form>

        {message && (
          <div
            className="text-sm p-3 rounded-lg mt-4"
            style={{
              color: status === 'success' ? colors.green : colors.red,
              backgroundColor: status === 'success' ? `${colors.green}20` : `${colors.red}20`,
              border: `1px solid ${status === 'success' ? colors.green : colors.red}40`,
            }}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
}
